import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Globe, RefreshCw, ExternalLink } from "lucide-react";
import { Switch } from "../ui/switch";
import { useApi } from "../../hooks/useApi";
import { api, web, errMsg, toList } from "../../lib/api";
import { Panel, Loader, ErrorNote, Btn, shortDate } from "./kit";

const PlayerToggles = ({ tid, players, hidden, onChange }) => (
  <ul className="divide-y divide-[#1E293B]" data-testid="public-page-players">
    {players.map((p) => {
      const shown = !hidden.includes(p.id);
      return (
        <li key={p.id} className="flex items-center justify-between gap-3 py-2.5">
          <span className="min-w-0 truncate text-sm text-white">#{p.jersey ?? "–"} {p.name}</span>
          <label className="flex items-center gap-2 text-xs text-slate-400">
            {shown ? "Shown" : "Hidden from public page"}
            <Switch data-testid={`public-player-switch-${p.id}`} checked={shown} onCheckedChange={async (v) => {
              try { await web.put(`/teams/${tid}/public-page/players/${p.id}`, { hidden: !v }); onChange(); } catch (e) { toast.error(errMsg(e)); }
            }} />
          </label>
        </li>
      );
    })}
  </ul>
);

/** Coach settings: turn the fan page on/off, set a city for search, hide players, refresh the copy. */
export const PublicPagePanel = ({ tid }) => {
  const s = useApi(`/teams/${tid}/public-page`, web);
  const roster = useApi(`/teams/${tid}/players`, api);
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setCity(s.data?.city ?? ""); }, [s.data?.city]);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); s.reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = (enabled) => run(() => web.put(`/teams/${tid}/public-page`, { enabled, city }), enabled ? "Public team page is on" : "Public team page is off");
  if (s.loading && !s.data) return <Panel><Loader /></Panel>;
  if (s.error) return <Panel><ErrorNote error={s.error} /></Panel>;
  const on = !!s.data?.enabled;
  const players = toList(roster.data, ["players"]).filter((p) => (p.status ?? "active") === "active");
  return (
    <Panel data-testid="public-page-panel">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-display text-xl font-bold uppercase text-white"><Globe className="h-5 w-5 text-[#39FF14]" />Public team page</h3>
          <p className="text-sm text-slate-400">Fans find your team from the homepage search and see the record, schedule, roster (name, number, position, class), season stats and trading cards. Never GPA, height, weight, notes or contact details.</p>
        </div>
        <Switch data-testid="public-page-switch" checked={on} disabled={busy} onCheckedChange={save} />
      </div>
      <label className="mb-4 block text-sm text-slate-300">City or school (helps fans search)
        <div className="mt-1.5 flex gap-2">
          <input data-testid="public-page-city" value={city} maxLength={80} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Pinellas Park, FL"
            className="w-full rounded-full border border-[#1E293B] bg-[#162032] px-4 py-2.5 text-sm text-white placeholder:text-slate-500" />
          <Btn data-testid="public-page-city-save" variant="ghost" disabled={busy} onClick={() => save(on)}>Save</Btn>
        </div>
      </label>
      {on && (
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-[#39FF14]/30 bg-[#39FF14]/5 p-3 text-sm">
          <Link to={`/team/${s.data.slug}`} target="_blank" data-testid="public-page-open" className="inline-flex items-center gap-1.5 font-semibold text-[#39FF14]">View fan page<ExternalLink className="h-3.5 w-3.5" /></Link>
          <span className="text-slate-400">Updated {s.data.refreshed_at ? shortDate(s.data.refreshed_at) : "—"}</span>
          <button data-testid="public-page-refresh" disabled={busy} onClick={() => run(() => web.post(`/teams/${tid}/public-page/refresh`), "Fan page updated")}
            className="ml-auto inline-flex items-center gap-1.5 text-slate-200 hover:text-white disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />Refresh now</button>
        </div>
      )}
      <p className="mb-1 font-mono-gd text-[11px] uppercase tracking-[0.2em] text-slate-400">Players on the public page</p>
      <PlayerToggles tid={tid} players={players} hidden={s.data?.hidden_players ?? []} onChange={s.reload} />
    </Panel>
  );
};
